/**
 * Add to Cart UX Enhancements for Smokey's
 * Improves user experience without breaking existing functionality
 */

(function() {
  'use strict';

  // Production Console Security Wrapper
  const isProduction = window.location.hostname !== 'localhost' &&
                       !window.location.hostname.includes('preview') &&
                       !window.location.hostname.includes('.myshopify.com/admin');

  if (isProduction && typeof console !== 'undefined') {
    if (!window._originalConsole) {
      window._originalConsole = {
        log: console.log,
        warn: console.warn,
        error: console.error,
        debug: console.debug,
        info: console.info
      };
      console.log = function() {};
      console.warn = function() {};
      console.debug = function() {};
      console.info = function() {};
      console.error = function(...args) {
        if (args.some(arg => arg instanceof Error)) {
          window._originalConsole.error.apply(console, args);
        }
      };
    }
  }

  // Configuration
  const CONFIG = {
    TOAST_DURATION: 3000,
    ANIMATION_DURATION: 300,
    FREE_DELIVERY_THRESHOLD: 4999, // in cents
    AUTO_OPEN_CART: false, // Disabled for Smokey's
    HAPTIC_ENABLED: false // Disabled for Smokey's
  };

  function init() {
    enhanceAddToCartButtons();
    enhanceDeliveryBar();
    setupMobileEnhancements();
    enhanceBundleSelection();

    // Re-run enhanceAddToCartButtons after a delay to catch any late-loaded content
    setTimeout(() => {
      enhanceAddToCartButtons();
    }, 500);
  }

  /**
   * Enforce inventory limits on add to cart requests
   */
  async function enforceInventoryLimits(url, config) {
    if (!config || !config.body) return config;

    try {
      // Parse the request body to get variant and quantity
      let variantId = null;
      let requestedQty = 1;

      if (typeof config.body === 'string') {
        const params = new URLSearchParams(config.body);
        variantId = params.get('id');
        requestedQty = parseInt(params.get('quantity') || '1', 10);
      }

      if (!variantId) return config;

      // Find the product card for this variant
      const cards = document.querySelectorAll('.ocs-card');
      let invMap = null;

      for (const card of cards) {
        const invEl = card.querySelector('.variant-inventory');
        if (invEl) {
          try {
            const tempMap = JSON.parse(invEl.textContent);
            if (tempMap[variantId]) {
              invMap = tempMap;
              break;
            }
          } catch (e) {}
        }
      }

      if (!invMap || !invMap[variantId]) return config;

      const meta = invMap[variantId];
      const policy = meta?.p || 'deny';
      const onHand = typeof meta?.q === 'number' ? meta.q : null;

      if (policy === 'deny' && onHand !== null) {
        // Get current cart quantity
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        let inCart = 0;

        for (const item of cart.items) {
          if (String(item.variant_id) === String(variantId)) {
            inCart += item.quantity;
          }
        }

        // Calculate remaining and clamp
        const remaining = Math.max(0, onHand - inCart);
        const finalQty = Math.min(requestedQty, remaining);

        if (finalQty < requestedQty) {
          // Update the request body with clamped quantity
          if (typeof config.body === 'string') {
            const params = new URLSearchParams(config.body);
            params.set('quantity', String(finalQty));
            config.body = params.toString();
          }
        }

        if (finalQty === 0) {
          throw new Error('Out of stock');
        }
      }
    } catch (err) {
      // On error, let the original request through
    }

    return config;
  }

  /**
   * Intercept fetch calls to detect add to cart and enforce inventory limits
   */
  function interceptAddToCart() {
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      let [url, config] = args;

      // Check if this is an add to cart request
      if (url && url.includes('/cart/add')) {
        // Try to enforce inventory limits
        config = await enforceInventoryLimits(url, config);
      }

      // Check if this is an add to cart request
      if (url && (url.includes('/cart/add') || url.includes('/cart/change'))) {
        // Call original fetch
        return originalFetch.apply(this, [url, config]).then(response => {
          // Clone response to read it
          const clonedResponse = response.clone();

          // If successful, trigger our enhancements
          if (response.ok) {
            clonedResponse.json().then(data => {
              // ONLY dispatch cart:added for actual ADD requests, not change/remove
              if (url.includes('/cart/add')) {
                // Trigger our custom event
                const event = new CustomEvent('cart:added', {
                  detail: {
                    product: data.product_title || data.title || 'Product',
                    quantity: data.quantity || 1,
                    variant: data.variant_id,
                    response: data
                  },
                  bubbles: true
                });
                document.dispatchEvent(event);

                // Show visual feedback
                const submitButton = document.querySelector('[type="submit"][name="add"]:not([disabled])');
                if (submitButton) {
                  showSuccessAnimation(submitButton);
                }
              }
            }).catch(err => {
              // Silent fail
            });
          }

          return response;
        });
      }

      // Not a cart request, proceed normally
      return originalFetch.apply(this, args);
    };
  }

  // Set up cart:added listener immediately (only once)
  let cartAddedListenerSetup = false;

  function setupCartAddedListener() {
    if (cartAddedListenerSetup) return;
    cartAddedListenerSetup = true;

    document.addEventListener('cart:added', (event) => {
      const { product, quantity, variant } = event.detail || {};

      // Only show toast if quantity is positive (item being added, not removed)
      if (quantity > 0) {
        showToastNotification(product, quantity);
      }

      // Update delivery bar with animation
      animateDeliveryBarUpdate();

      // DISABLED: Haptic feedback
      // if (CONFIG.HAPTIC_ENABLED && window.navigator.vibrate) {
      //   window.navigator.vibrate(50);
      // }

      // DISABLED: Auto-open cart drawer
      // if (CONFIG.AUTO_OPEN_CART && !sessionStorage.getItem('disable-auto-cart')) {
      //   setTimeout(() => openCartDrawer(), 300);
      // }
    });
  }

  // Set up the listener immediately
  setupCartAddedListener();

  /**
   * Enhance all add to cart buttons with better feedback
   */
  function enhanceAddToCartButtons() {
    // Ensure listener is set up
    setupCartAddedListener();

    // Enhance submit buttons
    document.querySelectorAll('[type="submit"][name="add"]').forEach(button => {
      if (!button.dataset.enhanced) {
        enhanceButton(button);
        button.dataset.enhanced = 'true';
      }
    });
  }

  /**
   * Add visual enhancements to buttons
   */
  function enhanceButton(button) {
    // Add ripple effect on click
    button.addEventListener('click', function(e) {
      const ripple = document.createElement('span');
      ripple.className = 'cart-ripple';
      ripple.style.left = e.offsetX + 'px';
      ripple.style.top = e.offsetY + 'px';
      button.appendChild(ripple);

      setTimeout(() => ripple.remove(), 600);
    });

    // Add loading state with progress
    const originalSubmit = button.closest('form')?.onsubmit;
    if (button.closest('form')) {
      button.closest('form').addEventListener('submit', function(e) {
        if (!button.classList.contains('loading')) {
          button.classList.add('loading-with-progress');
          updateButtonProgress(button);
        }
      });
    }
  }

  /**
   * Show success animation on the button
   */
  function showSuccessAnimation(element) {
    const button = element?.querySelector('[type="submit"]') || element;
    if (!button) return;

    // Add success class
    button.classList.add('add-success');

    // Create checkmark animation
    const checkmark = document.createElement('span');
    checkmark.className = 'success-checkmark';
    checkmark.innerHTML = `
      <svg viewBox="0 0 24 24" width="20" height="20">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
              fill="currentColor" stroke="currentColor" stroke-width="2"/>
      </svg>
    `;
    button.appendChild(checkmark);

    setTimeout(() => {
      button.classList.remove('add-success', 'loading-with-progress');
      checkmark.remove();
    }, 2000);
  }

  /**
   * Show toast notification for added items
   */
  function showToastNotification(product, quantity = 1) {
    // Remove any existing toast
    document.querySelector('.cart-toast')?.remove();

    const toast = document.createElement('div');
    toast.className = 'cart-toast';

    // Format product name
    const productName = typeof product === 'string' ? product : (product?.title || 'Product');

    toast.innerHTML = `
      <div class="toast-content">
        <svg class="toast-icon" viewBox="0 0 24 24" width="28" height="28">
          <circle cx="12" cy="12" r="11" fill="#660eae" opacity="0.1"/>
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#660eae" stroke="#660eae" stroke-width="0.5"/>
        </svg>
        <div class="toast-text">
          <strong>✓ ${quantity} ${quantity > 1 ? 'items' : 'item'} added to cart</strong>
          <span>${productName}</span>
        </div>
        <button class="toast-action">View Cart</button>
      </div>
    `;

    // Add click handler to the button after creating element
    document.body.appendChild(toast);

    const viewCartBtn = toast.querySelector('.toast-action');
    if (viewCartBtn) {
      viewCartBtn.addEventListener('click', () => {
        openCartDrawer();
        toast.remove();
      });
    }

    // Animate in
    requestAnimationFrame(() => {
      toast.classList.add('show');
    });

    // Auto dismiss
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, CONFIG.TOAST_DURATION);
  }

  /**
   * Enhance delivery bar with smooth animations
   */
  function enhanceDeliveryBar() {
    const deliveryBar = document.querySelector('.free-delivery-bar');
    if (!deliveryBar) return;

    // Add transition classes
    deliveryBar.classList.add('enhanced-delivery-bar');

    // Listen for cart updates
    document.addEventListener('cart:updated', async () => {
      const cart = await fetchCart();
      updateDeliveryBarWithAnimation(cart);
    });
  }

  /**
   * Animate delivery bar progress update
   */
  function animateDeliveryBarUpdate() {
    const progressBar = document.querySelector('.delivery-progress-fill');
    const amountText = document.querySelector('.delivery-amount-needed');

    if (progressBar) {
      progressBar.style.transition = 'width 0.5s ease-out';

      // Add pulse effect
      progressBar.classList.add('pulse');
      setTimeout(() => progressBar.classList.remove('pulse'), 1000);
    }

    if (amountText) {
      amountText.classList.add('updating');
      setTimeout(() => amountText.classList.remove('updating'), 500);
    }
  }

  /**
   * Setup mobile-specific enhancements
   */
  function setupMobileEnhancements() {
    if (!isMobile()) return;

    // Make add to cart button sticky on scroll
    const productForm = document.querySelector('product-form');
    if (productForm && window.scrollY > 300) {
      createStickyAddToCart(productForm);
    }

    // Add swipe to remove in cart drawer
    setupSwipeToRemove();
  }

  /**
   * Enhance bundle selection UI
   */
  function enhanceBundleSelection() {
    const bundleButtons = document.querySelectorAll('.bundle-option, .variant-button');

    bundleButtons.forEach(button => {
      // Add hover effects
      button.addEventListener('mouseenter', function() {
        this.style.transform = 'translateY(-2px)';
      });

      button.addEventListener('mouseleave', function() {
        this.style.transform = 'translateY(0)';
      });

      // Highlight selected with animation
      button.addEventListener('click', function() {
        // Remove previous selection animation
        bundleButtons.forEach(b => b.classList.remove('selecting'));

        // Add selection animation
        this.classList.add('selecting');

        // Show savings if it's a bundle
        const savings = this.dataset.savings;
        if (savings) {
          showSavingsBadge(this, savings);
        }
      });
    });
  }

  /**
   * Show savings badge animation
   */
  function showSavingsBadge(element, savings) {
    const badge = document.createElement('span');
    badge.className = 'savings-float';
    badge.textContent = `Save $${savings}!`;
    badge.style.position = 'absolute';
    badge.style.top = '-20px';
    badge.style.right = '10px';

    element.style.position = 'relative';
    element.appendChild(badge);

    // Animate up and fade
    requestAnimationFrame(() => {
      badge.style.animation = 'floatUp 2s ease-out forwards';
    });

    setTimeout(() => badge.remove(), 2000);
  }

  /**
   * Update button with loading progress
   */
  function updateButtonProgress(button) {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 20;
      button.style.setProperty('--progress', `${progress}%`);

      if (progress >= 100) {
        clearInterval(interval);
      }
    }, 100);
  }

  /**
   * Helper Functions
   */

  async function fetchCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch cart:', error);
      return null;
    }
  }

  function openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.open) {
      cartDrawer.open();
    }
    // Fallback: trigger click on cart icon
    else {
      document.querySelector('[id*="cart-icon"]')?.click();
    }
  }

  function isMobile() {
    return window.innerWidth <= 768 ||
           /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  function createStickyAddToCart(form) {
    const stickyContainer = document.createElement('div');
    stickyContainer.className = 'sticky-add-to-cart';
    stickyContainer.innerHTML = `
      <div class="sticky-content">
        <div class="product-info-mini">
          <span class="product-title-mini">${document.querySelector('h1')?.textContent || 'Product'}</span>
          <span class="product-price-mini">${document.querySelector('.price')?.textContent || ''}</span>
        </div>
        <button type="button" class="sticky-add-btn" onclick="document.querySelector('product-form [type=submit]').click()">
          Add to Cart
        </button>
      </div>
    `;

    document.body.appendChild(stickyContainer);

    // Show/hide based on scroll
    window.addEventListener('scroll', () => {
      if (window.scrollY > 300) {
        stickyContainer.classList.add('show');
      } else {
        stickyContainer.classList.remove('show');
      }
    });
  }

  function setupSwipeToRemove() {
    const cartItems = document.querySelectorAll('cart-drawer-items .cart-item');

    cartItems.forEach(item => {
      let startX = 0;
      let currentX = 0;
      let isDragging = false;

      item.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        isDragging = true;
      });

      item.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        currentX = e.touches[0].clientX;
        const diff = currentX - startX;

        if (diff < -50) {
          item.style.transform = `translateX(${diff}px)`;
          item.style.opacity = 1 + (diff / 200);
        }
      });

      item.addEventListener('touchend', () => {
        isDragging = false;
        const diff = currentX - startX;

        if (diff < -100) {
          // Remove item
          item.style.transform = 'translateX(-100%)';
          item.style.opacity = '0';
          setTimeout(() => {
            item.querySelector('cart-remove-button button')?.click();
          }, 300);
        } else {
          // Snap back
          item.style.transform = 'translateX(0)';
          item.style.opacity = '1';
        }
      });
    });
  }

  // Global cart icon update function
  function updateCartIcon() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const count = cart.item_count;

        // Update visible count
        const bubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
        if (bubble) {
          bubble.textContent = count;
        }

        // Update screen reader count
        const srBubble = document.querySelector('.cart-count-bubble .visually-hidden');
        if (srBubble) {
          srBubble.textContent = count + ' items';
        }

        // Show/create bubble if it doesn't exist
        let bubbleContainer = document.querySelector('.cart-count-bubble');
        if (!bubbleContainer && count > 0) {
          const cartIcon = document.getElementById('cart-icon-bubble');
          if (cartIcon) {
            bubbleContainer = document.createElement('div');
            bubbleContainer.className = 'cart-count-bubble';
            bubbleContainer.innerHTML = `
              <span aria-hidden="true">${count}</span>
              <span class="visually-hidden">${count} items</span>
            `;
            cartIcon.appendChild(bubbleContainer);
          }
        }

        // Show/hide bubble based on count
        if (bubbleContainer) {
          bubbleContainer.style.display = count > 0 ? '' : 'none';
        }
      })
      .catch(err => {
        // Silent fail
      });
  }

  // Listen for cart events and update icon
  document.addEventListener('cart:added', updateCartIcon);
  document.addEventListener('cart:refresh', updateCartIcon);
  document.addEventListener('cart:updated', updateCartIcon);

  // CRITICAL: Update cart icon on back/forward button navigation
  window.addEventListener('pageshow', (event) => {
    updateCartIcon();
    if (event.persisted) {
      setTimeout(() => updateCartIcon(), 100);
    }
  });

  // Export for external use
  window.SmokeysCartUX = {
    showToast: showToastNotification,
    openCart: openCartDrawer,
    enhanceButton: enhanceButton,
    updateCartIcon: updateCartIcon
  };

  // Make updateCartIcon globally available
  window.updateCartIcon = updateCartIcon;

  // Initialize immediately - intercept fetch and set up event listeners
  interceptAddToCart();
  setupCartAddedListener();

  // Initialize DOM-dependent features when ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Add a small delay to ensure other scripts are loaded
    setTimeout(init, 100);
  }

  /* ---------- BOOTLOADER: make add-to-cart work on first click, no refresh ---------- */
  (() => {
    // Guards so we don't double-install
    if (window.__smokeysBootInstalled) return;
    window.__smokeysBootInstalled = true;

    const once = (k, fn) => {
      if (window[k]) return;
      window[k] = true;
      fn();
    };

    // Ensure fetch interceptor is the outermost and only installed once
    once('__cartFetchPatched', () => {
      try { interceptAddToCart && interceptAddToCart(); } catch (e) { /* Silent */ }
    });

    // Ensure event listener exists before any add happens
    once('__cartAddedListener', () => {
      try { setupCartAddedListener && setupCartAddedListener(); } catch (e) { /* Silent */ }
    });

    // Throttled re-enhance (for sections/turbo/mutations)
    let rebindTimer = null;
    const rebind = () => {
      clearTimeout(rebindTimer);
      rebindTimer = setTimeout(() => {
        try { enhanceAddToCartButtons && enhanceAddToCartButtons(); } catch (e) { /* Silent */ }
      }, 50);
    };

    // Run once now (covers initial page)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', rebind, { once: true });
    } else {
      rebind();
    }

    // Re-run when Shopify re-renders sections/app blocks
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:select',
     'shopify:section:deselect', 'shopify:section:reorder', 'shopify:section:render'
    ].forEach(evt => document.addEventListener(evt, rebind));

    // Re-run on Turbo/Turbolinks navigations (if present)
    document.addEventListener('turbo:load', rebind);
    document.addEventListener('turbolinks:load', rebind);
    window.addEventListener('pageshow', rebind);

    // Re-run when cards are injected/changed (apps, infinite scroll, facet filters)
    const mo = new MutationObserver(muts => {
      for (const m of muts) {
        if (m.addedNodes && m.addedNodes.length) { rebind(); break; }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });

    // Optional: mark actively submitting form so the success reset targets the right card
    document.addEventListener('submit', (e) => {
      const f = e.target?.closest?.('form');
      if (f) window.__activeAddForm = f;
    }, true);
  })();

})();